import type { ReactNode } from 'react';

// Micro-visuals sized for a stat tile: plain SVG/CSS on the design tokens (no chart library),
// each paired with text so colour is never the only signal (WCAG 1.4.1). Tones: `pos` / `warn` /
// `neg` are reserved for state, the accent for progress.
//
//   Gauge       ring against a target (billable hours vs the weekly target)
//   Meter       a bar that fills by CONSUMPTION — nearly full = nearly used up — with the
//               headline staying "x left"; `invert` makes it a count-down
//   SplitDonut  part of a whole (billable share of logged time)
//   SegmentBar  proportions of a few categories (stale tickets by severity)
//   CategoryBars horizontal bars by category (booked time by appointment type)
//   DayBars     a bar per day, optionally stacked primary/secondary (billable/other)
//   Delta       ▴/▾ change or distance to a target, toned by whether it is good
//   Sparkline   a trend line over a short series (a KPI's 12 months); gaps where there is no data

export type VizTone = 'accent' | 'pos' | 'warn' | 'neg' | 'info' | 'muted';
const toneVar = (t: VizTone) => (t === 'muted' ? 'var(--ink-3)' : `var(--${t})`);
const clamp01 = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

/** Ring gauge of value against target. Over target turns positive. */
export function Gauge({ value, target, label, size = 96, tone }: { value: number; target: number; label?: ReactNode; size?: number; tone?: VizTone }) {
  const ratio = target > 0 ? value / target : 0;
  const t: VizTone = tone ?? (ratio >= 1 ? 'pos' : 'accent');
  const r = 42;
  const c = 2 * Math.PI * r;
  return (
    <span className="hf-gauge" style={{ width: size, height: size }} role="img" aria-label={`${Math.round(ratio * 100)}% of target`}>
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
        <circle cx="50" cy="50" r={r} className="track" />
        <circle cx="50" cy="50" r={r} className="fill" style={{ stroke: toneVar(t), strokeDasharray: `${clamp01(ratio) * c} ${c}` }} transform="rotate(-90 50 50)" />
      </svg>
      {label != null && <span className="centre">{label}</span>}
    </span>
  );
}

/**
 * A meter filled by consumption: `used` of `total`. Overdrawn (used > total) is negative; little
 * left (remaining ≤ warnBelow of total) is a warning. `invert` fills by what REMAINS instead.
 */
export function Meter({ used, total, invert, warnBelow = 0.15, label }: { used: number; total: number; invert?: boolean; warnBelow?: number; label?: ReactNode }) {
  const consumed = total > 0 ? used / total : used > 0 ? 1 : 0;
  const remaining = 1 - consumed;
  const tone: VizTone = consumed > 1 ? 'neg' : remaining <= warnBelow ? 'warn' : 'accent';
  const fill = clamp01(invert ? remaining : consumed);
  return (
    <span className="hf-meter" role="meter" aria-valuemin={0} aria-valuemax={total} aria-valuenow={used} aria-label={typeof label === 'string' ? label : undefined}>
      <span className="bar"><span className="fill" style={{ width: `${fill * 100}%`, background: toneVar(tone) }} /></span>
      {label != null && <span className="lbl">{label}</span>}
    </span>
  );
}

/** Part of a whole as a donut, the centre carrying the headline. */
export function SplitDonut({ part, whole, centre, size = 96, tone = 'accent' }: { part: number; whole: number; centre?: ReactNode; size?: number; tone?: VizTone }) {
  const share = whole > 0 ? part / whole : 0;
  const r = 40;
  const c = 2 * Math.PI * r;
  return (
    <span className="hf-gauge split" style={{ width: size, height: size }} role="img" aria-label={`${Math.round(share * 100)}%`}>
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden>
        <circle cx="50" cy="50" r={r} className="track" />
        <circle cx="50" cy="50" r={r} className="fill" style={{ stroke: toneVar(tone), strokeDasharray: `${clamp01(share) * c} ${c}` }} transform="rotate(-90 50 50)" />
      </svg>
      {centre != null && <span className="centre">{centre}</span>}
    </span>
  );
}

export interface Segment {
  key: string;
  label: string;
  value: number;
  tone: VizTone;
}

/** Proportions of a few categories in one bar, with a counted legend. */
export function SegmentBar({ segments, legend = true, format = (s) => s.value }: { segments: Segment[]; legend?: boolean; format?: (s: Segment) => ReactNode }) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  return (
    <span className="hf-segbar">
      <span className="bar" role="img" aria-label={segments.map((s) => `${s.label} ${s.value}`).join(', ')}>
        {total > 0 && segments.filter((s) => s.value > 0).map((s) => <span key={s.key} style={{ flexGrow: s.value, background: toneVar(s.tone) }} />)}
      </span>
      {legend && (
        <span className="legend">
          {segments.map((s) => (
            <span key={s.key}>
              <i style={{ background: toneVar(s.tone) }} aria-hidden />
              {s.label} <b className="hf-num">{format(s)}</b>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

/** Horizontal bars by category, longest first is the caller's order. */
export function CategoryBars({ items, format = (n) => String(n) }: { items: { key: string; label: string; value: number; tone?: VizTone }[]; format?: (n: number) => string }) {
  const max = Math.max(0, ...items.map((i) => i.value));
  return (
    <span className="hf-catbars">
      {items.map((i) => (
        <span key={i.key} className="row">
          <span className="lbl">{i.label}</span>
          <span className="bar"><span style={{ width: `${max > 0 ? (i.value / max) * 100 : 0}%`, background: toneVar(i.tone ?? 'accent') }} /></span>
          <span className="hf-num val">{format(i.value)}</span>
        </span>
      ))}
    </span>
  );
}

/** A bar per day; `secondary` stacks above `value` (e.g. billable then other). */
export function DayBars({ days, format = (n) => String(n), height = 56 }: { days: { key: string; label: string; value: number; secondary?: number }[]; format?: (n: number) => string; height?: number }) {
  const max = Math.max(0, ...days.map((d) => d.value + (d.secondary ?? 0)));
  return (
    <span className="hf-daybars" style={{ height: height + 18 }}>
      {days.map((d) => {
        const total = d.value + (d.secondary ?? 0);
        return (
          <span key={d.key} className="day" title={`${d.label}: ${format(total)}`}>
            <span className="stack" style={{ height }}>
              {d.secondary ? <span className="sec" style={{ height: `${max > 0 ? (d.secondary / max) * 100 : 0}%` }} /> : null}
              <span className="pri" style={{ height: `${max > 0 ? (d.value / max) * 100 : 0}%` }} />
            </span>
            <span className="lbl">{d.label}</span>
          </span>
        );
      })}
    </span>
  );
}

/** ▴/▾ with a quantity; `good` says which direction is good (default up). */
export function Delta({ value, children, good = 'up' }: { value: number; children: ReactNode; good?: 'up' | 'down' }) {
  if (!Number.isFinite(value) || value === 0) return <span className="hf-delta muted">{children}</span>;
  const up = value > 0;
  const tone = (up && good === 'up') || (!up && good === 'down') ? 'pos' : 'warn';
  return (
    <span className={`hf-delta ${tone}`}>
      <span aria-hidden>{up ? '▴' : '▾'}</span> {children}
    </span>
  );
}

/** A small trend line; null points are gaps. The last point is marked. */
export function Sparkline({ values, label, width = 120, height = 32, tone = 'accent' }: { values: (number | null)[]; label: string; width?: number; height?: number; tone?: VizTone }) {
  const nums = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (nums.length === 0) return <span className="hf-spark empty" role="img" aria-label={`${label}: no data`} />;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const step = values.length > 1 ? (width - 4) / (values.length - 1) : 0;
  const pt = (v: number, i: number) => `${(2 + i * step).toFixed(1)},${(height - 2 - ((v - min) / span) * (height - 4)).toFixed(1)}`;
  const segments: string[][] = [];
  let cur: string[] = [];
  values.forEach((v, i) => {
    if (v === null || !Number.isFinite(v)) {
      if (cur.length) segments.push(cur);
      cur = [];
    } else cur.push(pt(v, i));
  });
  if (cur.length) segments.push(cur);
  const lastIdx = values.map((v, i) => (v !== null && Number.isFinite(v) ? i : -1)).filter((i) => i >= 0).pop()!;
  const [lx, ly] = pt(values[lastIdx] as number, lastIdx).split(',');
  return (
    <span className="hf-spark" role="img" aria-label={label}>
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden>
        {segments.map((seg, i) => (seg.length > 1 ? <polyline key={i} points={seg.join(' ')} fill="none" style={{ stroke: toneVar(tone) }} /> : null))}
        <circle cx={lx} cy={ly} r={2.5} style={{ fill: toneVar(tone) }} />
      </svg>
    </span>
  );
}
